import { useState } from 'react';
import '../assets/styles/DevisPanel.css';
import { lireFormulaireDepuisTexte } from '../services/iaService';
import ChampMentions from './ChampMentions';

/**
 * Zone « coller un texte » des formulaires de modification : l'IA lit le
 * message collé et reporte les valeurs trouvées dans les champs. Une
 * proposition — l'utilisateur relit puis enregistre comme d'habitude.
 *
 * `champs`   : [{ cle, libelle, type?: texte|nombre|date|choix|email|telephone, options? }]
 * `variante` : 'devis' (défaut) ou 'param' selon la feuille de style de la modale.
 * `onValeurs`: reçoit { cle: valeur } pour les seuls champs trouvés.
 * `formations` + `onLignes` (formulaires de devis) : le texte peut aussi décrire
 * les LIGNES du devis — formation, format, durée, effectif, prix, remises,
 * options (plusieurs par formation). `formations` : [{ id, code, intitule }] ;
 * `onLignes` reçoit les lignes lues, déjà rapprochées du catalogue.
 */
function RemplirParTexte({ champs, onValeurs, contexte, aide, variante = 'devis', formations, onLignes }) {
  const bouton = variante === 'param' ? 'param-btn' : 'devis-btn devis-btn-sm';
  const primaire = variante === 'param' ? 'param-btn param-btn-primary' : 'devis-btn devis-btn-sm devis-btn-primary';
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [retour, setRetour] = useState(null); // { ok, texte }

  async function lancer() {
    if (!texte.trim()) return;
    setEnCours(true);
    setRetour(null);
    try {
      const { valeurs, lignes, ignorees } = await lireFormulaireDepuisTexte({
        texte,
        champs,
        contexte,
        formations: onLignes ? formations : undefined,
      });
      const cles = Object.keys(valeurs);
      const nbLignes = onLignes ? lignes.length : 0;
      if (cles.length || nbLignes) {
        if (cles.length) onValeurs(valeurs);
        if (nbLignes) onLignes(lignes);
        const noms = cles.map((c) => champs.find((x) => x.cle === c)?.libelle || c);
        const options = lignes.filter((l) => l.estOption).length;
        const morceaux = [
          noms.length ? `Champs remplis : ${noms.join(', ')}` : null,
          nbLignes
            ? `${nbLignes} ligne${nbLignes > 1 ? 's' : ''} du devis lue${nbLignes > 1 ? 's' : ''}` +
              (options ? ` dont ${options} option${options > 1 ? 's' : ''}` : '') +
              ' (elles remplacent les lignes actuelles)'
            : null,
          ignorees.length ? `formation(s) introuvable(s) au catalogue : ${ignorees.join(', ')}` : null,
        ].filter(Boolean);
        setRetour({ ok: true, texte: `${morceaux.join(' · ')}. Relisez avant d’enregistrer.` });
        setTexte('');
      } else {
        setRetour({ ok: false, texte: 'L’IA n’a trouvé aucune modification à reporter dans ce texte.' });
      }
    } catch (err) {
      setRetour({ ok: false, texte: err.message || 'Lecture par l’IA impossible.' });
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="remplir-texte">
      <button
        type="button"
        className={bouton}
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
      >
        {ouvert ? 'Masquer la saisie par texte' : 'Coller un texte (remplissage par l’IA)'}
      </button>
      {ouvert && (
        <div className="remplir-texte-corps">
          <ChampMentions
            rows={5}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            suffixe={'\n'}
            placeholder={
              aide || 'Collez ici le mail ou le message contenant les modifications : l’IA les reporte dans les champs.'
            }
            disabled={enCours}
          />
          <div className="remplir-texte-actions">
            <button
              type="button"
              className={primaire}
              onClick={lancer}
              disabled={enCours || !texte.trim()}
            >
              {enCours ? 'Lecture par l’IA…' : 'Remplir avec l’IA'}
            </button>
          </div>
          {retour && (
            <div className={`remplir-texte-retour${retour.ok ? '' : ' remplir-texte-erreur'}`}>
              {retour.texte}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RemplirParTexte;
